import { useQuery } from '@tanstack/react-query';
import { FormField, SegmentedControl, Select } from '@/shared/components';
import { useApi } from '@/shared/api';
import { PropertyAddressAutocomplete } from '../property-form/PropertyAddressAutocomplete';
import {
  isCompleteAddress,
  suggestObjectName,
  type LocationMode,
  type WizardForm,
} from './wizardModel';
import { WizardField } from './WizardField';

interface StepLocationProps {
  form: WizardForm;
  patch: (partial: Partial<WizardForm>) => void;
  errors: Record<string, string>;
}

export function StepLocation({ form, patch, errors }: StepLocationProps) {
  const api = useApi();
  const groups = useQuery({ queryKey: ['property-groups'], queryFn: api.propertyGroups.list });
  const patchManualAddress = (partial: Partial<WizardForm>) => {
    const address = { ...form, ...partial };
    patch({
      ...partial,
      latitude: null,
      longitude: null,
      needsAddressReview: !isCompleteAddress(address),
    });
  };

  return (
    <div className="space-y-5">
      <SegmentedControl<LocationMode>
        value={form.locationMode}
        onChange={(locationMode) => patch({ locationMode })}
        options={[
          { value: 'existing', label: 'Bestehendes Objekt' },
          { value: 'new', label: 'Neuer Standort' },
        ]}
      />

      {form.locationMode === 'existing' ? (
        <FormField label="Objekt" error={errors.existingObjectId} required>
          <Select
            value={form.existingObjectId}
            onChange={(event) => {
              const id = event.target.value;
              const group = groups.data?.find((g) => g.id === id);
              patch({
                existingObjectId: id,
                existingObjectLabel: group ? `${group.name} — ${group.address.street}` : '',
              });
            }}
          >
            <option value="">Objekt wählen …</option>
            {(groups.data ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name} — {group.address.street}, {group.address.postalCode}{' '}
                {group.address.city}
              </option>
            ))}
          </Select>
        </FormField>
      ) : (
        <div className="space-y-4">
          <FormField label="Adresse suchen">
            <PropertyAddressAutocomplete
              value={form.addressQuery}
              onChange={(addressQuery) => patch({ addressQuery })}
              onSelect={(s) => {
                const a = s.address;
                patch({
                  addressQuery: s.label,
                  streetName: a.streetName,
                  houseNumber: a.houseNumber,
                  postalCode: a.postalCode,
                  city: a.city,
                  countryCode: a.countryCode,
                  latitude: a.latitude ?? null,
                  longitude: a.longitude ?? null,
                  needsAddressReview: s.needsAddressReview,
                  objectName:
                    form.objectName.trim() ||
                    suggestObjectName(a.streetName, a.houseNumber, a.city),
                });
              }}
            />
          </FormField>
          <div className="grid gap-4 md:grid-cols-2">
            <WizardField
              label="Straße"
              value={form.streetName}
              error={errors.streetName}
              required
              onChange={(streetName) => patchManualAddress({ streetName })}
            />
            <WizardField
              label="Hausnummer"
              value={form.houseNumber}
              error={errors.houseNumber}
              required
              onChange={(houseNumber) => patchManualAddress({ houseNumber })}
            />
            <WizardField
              label="PLZ"
              value={form.postalCode}
              error={errors.postalCode}
              required
              onChange={(postalCode) => patchManualAddress({ postalCode })}
            />
            <WizardField
              label="Stadt"
              value={form.city}
              error={errors.city}
              required
              onChange={(city) => patchManualAddress({ city })}
            />
            <FormField label="Land" error={errors.countryCode} required>
              <Select
                value={form.countryCode}
                onChange={(e) => patchManualAddress({ countryCode: e.target.value })}
              >
                <option value="DE">DE — Deutschland</option>
                <option value="AT">AT — Österreich</option>
                <option value="CH">CH — Schweiz</option>
              </Select>
            </FormField>
            <WizardField
              label="Objektname"
              value={form.objectName}
              error={errors.objectName}
              required
              hint="Vorbelegt aus Straße + Hausnummer, editierbar."
              onChange={(objectName) => patch({ objectName })}
            />
          </div>
          {form.needsAddressReview && (
            <p
              className="border-sealwax/30 bg-sealwax/5 text-ink rounded-md border p-3 text-sm"
              role="status"
            >
              Adresse vervollständigen, damit sie freigegeben werden kann.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
